/**
 * Maps a MIME type to one of the categories `files.fileType.*` translates —
 * exhaustive against `ALLOWED_MIME_TYPES` in the file-service, the actual
 * set of types the platform will ever store (an allow-list, so nothing
 * outside it can reach this code).
 */
export type FileTypeCategory =
  | 'IMAGE'
  | 'PDF'
  | 'TEXT'
  | 'DOCUMENT'
  | 'SPREADSHEET'
  | 'PRESENTATION'
  | 'AUDIO'
  | 'VIDEO'
  | 'OTHER';

const CATEGORY_BY_MIME: Record<string, FileTypeCategory> = {
  'image/jpeg': 'IMAGE',
  'image/png': 'IMAGE',
  'image/gif': 'IMAGE',
  'image/webp': 'IMAGE',
  'image/heic': 'IMAGE',
  'application/pdf': 'PDF',
  'text/plain': 'TEXT',
  'text/csv': 'TEXT',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCUMENT',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'SPREADSHEET',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PRESENTATION',
  'audio/mpeg': 'AUDIO',
  'audio/mp4': 'AUDIO',
  'video/mp4': 'VIDEO',
  'video/quicktime': 'VIDEO',
};

export function fileTypeCategory(mimeType: string): FileTypeCategory {
  return CATEGORY_BY_MIME[mimeType.toLowerCase()] ?? 'OTHER';
}

/**
 * Whether the browser can render this file inline (`<img>`, `<iframe>`,
 * `<video>`, `<audio>`) from a signed URL alone. HEIC is excluded despite
 * being an image: Chrome and Firefox do not decode it in an `<img>` tag.
 * Office formats (Word/Excel/PowerPoint) have no built-in browser renderer.
 */
export function isPreviewable(mimeType: string): boolean {
  const type = mimeType.toLowerCase();
  if (type === 'image/heic') return false;
  const category = fileTypeCategory(type);
  return category === 'IMAGE' || category === 'PDF' || category === 'VIDEO' || category === 'AUDIO';
}

/** Same IMAGE/HEIC carve-out as `isPreviewable` — the row thumbnail can
 *  show the real picture only for a MIME type `<img>` actually decodes. */
export function hasImageThumbnail(mimeType: string): boolean {
  const type = mimeType.toLowerCase();
  return fileTypeCategory(type) === 'IMAGE' && type !== 'image/heic';
}
